import React from "react";
import { createPortal } from "react-dom";
import styles from "./DrawerPortal.module.css";

interface DrawerPortalProps {
  children: React.ReactNode;
  /** Override z-index for pickers that must appear above MDialog (default: 99999999). */
  containerZIndex?: number;
}

const DEFAULT_Z_INDEX = 99999999;

/**
 * Returns (and lazily creates) a singleton DOM node for the given z-index bucket.
 * Separate containers allow pickers (higher z-index) to layer above dialogs while
 * regular drawers (default z-index) stay below them.
 */
function getPortalContainer(zIndex: number): HTMLElement {
  const id = `drawer-portal-container-${zIndex}`;
  let container = document.getElementById(id);
  if (!container) {
    container = document.createElement("div");
    container.id = id;
    container.style.position = "fixed";
    container.style.top = "0";
    container.style.left = "0";
    container.style.width = "100%";
    container.style.height = "100%";
    container.style.pointerEvents = "none";
    container.style.zIndex = String(zIndex);
    document.body.appendChild(container);
  }
  return container;
}

/**
 * DrawerPortal renders its children at the root level of the DOM to avoid
 * z-index stacking context issues. The portal container is created synchronously
 * on first use so there is no blank render frame between mount and content appearing.
 */
const DrawerPortal: React.FC<DrawerPortalProps> = ({
  children,
  containerZIndex,
}) => {
  const zIndex = containerZIndex ?? DEFAULT_Z_INDEX;
  const containerRef = React.useRef<HTMLElement | null>(null);
  if (!containerRef.current) {
    containerRef.current = getPortalContainer(zIndex);
  }

  return createPortal(
    <div className={styles.portalContent}>{children}</div>,
    containerRef.current
  );
};

export default DrawerPortal;
